import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, BadgeCheck, MapPin, CalendarPlus, Volume2, VolumeX } from "lucide-react";
import { api, setPageMeta } from "@/lib/api";
import { MaskedLines, Reveal, Counter } from "@/components/Motion";
import { Ticker } from "@/components/Ticker";
import { JoinForm } from "@/components/JoinForm";
import { BundleBuilder } from "@/components/BundleBuilder";
import { VideoTile } from "@/components/VideoTile";
import { SectionVideo } from "@/components/SectionVideo";
import { HeroVideo } from "@/components/HeroVideo";
import { Mark } from "@/components/Logo";

const CITIES = [
  ["Dallas", "/run-clubs/texas/dallas"],
  ["Austin", "/run-clubs/texas/austin"],
  ["Houston", "/run-clubs/texas/houston"],
  ["New York", "/run-clubs/new-york/new-york-city"],
  ["Los Angeles", "/run-clubs/california/los-angeles"],
  ["Chicago", "/run-clubs/illinois/chicago"],
  ["Boston", "/run-clubs/massachusetts/boston"],
  ["Miami", "/run-clubs/florida/miami"],
  ["Atlanta", "/run-clubs/georgia/atlanta"],
  ["Denver", "/run-clubs/colorado/denver"],
  ["Seattle", "/run-clubs/washington/seattle"],
  ["San Francisco", "/run-clubs/california/san-francisco"],
  ["Washington DC", "/run-clubs/district-of-columbia/washington-dc"],
  ["Philadelphia", "/run-clubs/pennsylvania/philadelphia"],
  ["San Diego", "/run-clubs/california/san-diego"],
  ["Portland", "/run-clubs/oregon/portland"],
  ["Charlotte", "/run-clubs/north-carolina/charlotte"],
  ["Nashville", "/run-clubs/tennessee/nashville"],
  ["Detroit", "/run-clubs/michigan/detroit"],
  ["Phoenix", "/run-clubs/arizona/phoenix"],
  ["Tampa", "/run-clubs/florida/tampa"],
  ["West Palm Beach", "/run-clubs/florida/west-palm-beach"],
];

const GOALS = ["5K", "10K", "Half Marathon", "Marathon", "HYROX", "Ironman", "Trail", "Ultra", "Beginner"];
const GOAL_LINKS = {
  "5K": "/training/category/5k", "10K": "/training/category/10k", "Half Marathon": "/training/category/half-marathon",
  Marathon: "/training/category/marathon", HYROX: "/training/category/hyrox", Ironman: "/training/category/ironman",
  Trail: "/clubs?tag=trail", Ultra: "/training/category/ultra", Beginner: "/training/category/start-running",
};

const VALUES = [
  ["SHOW UP", "Consistency starts before the first mile."],
  ["RUN TOGETHER", "Progress is personal, but the journey is shared."],
  ["EARN THE MILE", "We respect the work and do not look for shortcuts."],
  ["LEAVE NO RUNNER BEHIND", "Every pace has a place here."],
  ["GET BETTER", "Learn, adjust, and return stronger."],
  ["BUILD THE COMMUNITY", "Give more energy than you take."],
];

const FILM = [
  { src: "/videos/miles-01.mp4", poster: "/videos/miles-01-poster.jpg", caption: "SANDBAGS AT SUNRISE" },
  { src: "/videos/miles-02.mp4", poster: "/videos/miles-02-poster.jpg", caption: "MID-RACE" },
  { src: "/videos/miles-03.mp4", poster: "/videos/miles-03-poster.jpg", caption: "UNDER FATIGUE" },
  { src: "/videos/miles-04.mp4", poster: "/videos/miles-04-poster.jpg", caption: "THE LAST STATION" },
  { src: "/videos/miles-05.mp4", poster: "/videos/miles-05-poster.jpg", caption: "THE PACK" },
];

const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [sound, setSound] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.2]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[calc(100vh-4rem)] min-h-[600px] overflow-hidden bg-ink">
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <HeroVideo sound={sound} />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/50" />
      </motion.div>

      {!reduce && (
        <button
          data-testid="hero-sound-toggle"
          onClick={() => setSound((s) => !s)}
          aria-label={sound ? "Mute hero video" : "Unmute hero video"}
          className="absolute bottom-6 right-5 lg:right-10 z-20 flex items-center gap-2.5 border border-white/25 bg-ink/40 backdrop-blur-md px-4 py-3 text-white/70 hover:text-white hover:border-white transition-colors duration-200"
        >
          {sound ? <Volume2 size={15} /> : <VolumeX size={15} />}
          <span className="font-mono-label hidden sm:block">{sound ? "SOUND ON" : "SOUND OFF"}</span>
        </button>
      )}

      <div className="relative z-10 flex h-full flex-col justify-end px-5 lg:px-10 pb-16">
        <motion.p
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="font-mono-label text-white/70 mb-6"
        >
          A RUNNING MOVEMENT BORN IN DALLAS
        </motion.p>
        <MaskedLines
          as="h1"
          lines={["BETTER", "WITH EVERY", "MILE."]}
          lineClassName="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
          delay={0.2}
        />
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7 }}
          className="mt-6 max-w-xl text-white/70 text-base"
        >
          Built for every pace, every goal, and every city. Find your people, put in the miles, and bring others with you.
        </motion.p>
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-8 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/clubs"
            data-testid="hero-find-run-cta"
            className="btn-invert bg-white text-ink font-mono-label px-8 py-4 text-center border border-white hover:bg-transparent hover:text-white"
          >
            FIND A RUN
          </Link>
          <Link
            to="/join"
            data-testid="hero-join-cta"
            className="btn-invert border border-white/40 text-white font-mono-label px-8 py-4 text-center hover:bg-white hover:text-ink"
          >
            JOIN NINJA RUN CLUB
          </Link>
        </motion.div>
        <motion.p
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="font-mono-label text-white/40 mt-10"
        >
          DALLAS BORN. RUNNING EVERYWHERE.
        </motion.p>
      </div>
    </section>
  );
};

const NextRun = ({ event }) => {
  if (!event) return null;
  const d = new Date(event.date + "T12:00:00");
  const dateStr = d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const gcal = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title + " - Ninja Run Club")}&dates=${event.date.replace(/-/g, "")}/${event.date.replace(/-/g, "")}&details=${encodeURIComponent(event.description)}`;

  return (
    <section data-testid="next-dallas-run" className="relative bg-ink border-b border-white/10 px-5 lg:px-10 py-20 lg:py-28">
      <SectionVideo src="/video/run-04.mp4" srcMobile="/video/run-04-mobile.mp4" poster="/video/run-04-poster.jpg" opacity={0.28} testid="section-video-next-run" />
      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="font-mono-label text-white/40">NEXT DALLAS RUN</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-none">{event.title}</h2>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="space-y-4 border-l border-white/20 pl-6">
              <p className="font-mono-label text-white/80">{dateStr.toUpperCase()} · {event.time} {event.timezone}</p>
              <p className="text-white/60 text-sm flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0" /> {event.location}</p>
              <p className="text-white/60 text-sm">{(event.distances || []).join(" · ")}</p>
              <p className="text-white/60 text-sm">{event.pace}</p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-3 flex flex-col gap-3 lg:items-end">
          <Reveal delay={0.2}>
            <Link to="/dallas" data-testid="next-run-rsvp-link" className="btn-invert inline-block bg-white text-ink font-mono-label px-8 py-4 border border-white hover:bg-transparent hover:text-white">
              RSVP IN DALLAS HUB
            </Link>
            <a href={gcal} target="_blank" rel="noopener noreferrer" data-testid="next-run-calendar-link" className="mt-3 flex items-center gap-2 font-mono-label text-white/50 hover:text-white transition-colors duration-200">
              <CalendarPlus size={14} /> ADD TO CALENDAR
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [products, setProducts] = useState([]);
  const [spotIndex, setSpotIndex] = useState(0);

  useEffect(() => {
    setPageMeta("Ninja Run Club | Find Run Clubs Near You Across America", "A running movement born in Dallas. Search a human-verified directory of run clubs across America, evidence-backed training, free tools, and a community for every pace.");
    api.get("/events/next-dallas").then((r) => setEvent(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/articles").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
    api.get("/clubs/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data.slice(0, 4))).catch(() => {});
  }, []);

  const spotlight = featured[spotIndex % (featured.length || 1)];

  return (
    <div className="bg-night">
      <Hero />
      <Ticker items={["NEXT RUN · DALLAS", "DALLAS", "AUSTIN", "NEW YORK", "LOS ANGELES", "CHICAGO", "BOSTON", "MIAMI", "SAN FRANCISCO", "SEATTLE", "EVERY PACE HAS A PLACE"]} />
      <NextRun event={event} />

      {/* Community statement - light */}
      <section data-testid="community-statement" className="light-section bg-paper text-ink px-5 lg:px-10 py-24 lg:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="font-mono-label text-ink/40">THE POINT</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-4xl leading-[0.95]">
                YOU DO NOT HAVE TO BE FAST. YOU HAVE TO SHOW UP.
              </h2>
              <p className="mt-8 max-w-xl text-ink/60">
                Some of us are training for a first 5K. Some are chasing a marathon PR, a HYROX podium, an Ironman finish, or the confidence to run one more block. Different goals. Same road.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.15}>
              <VideoTile src="/video/run-01.mp4" srcMobile="/video/run-01-mobile.mp4" srcWebm="/video/run-01.webm" poster="/video/run-01-poster.jpg" caption="TRACK PACK" className="aspect-[4/5] w-full" testid="community-video" />
            </Reveal>
          </div>
        </div>
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10 mt-16">
            {[
              [stats.clubs, "CLUBS IN THE DIRECTORY"],
              [stats.cities, "CITIES AND GROWING"],
              [stats.articles, "TRAINING GUIDES"],
              [21, "FREE TOOLS"],
            ].map(([n, label]) => (
              <div key={label} className="bg-paper p-8">
                <Counter to={n} className="font-display text-4xl lg:text-5xl" />
                <p className="font-mono-label text-ink/40 mt-2">{label}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Find your crew */}
      <section data-testid="find-your-crew" className="relative bg-ink px-5 lg:px-10 py-24 lg:py-32 border-b border-white/10">
        <SectionVideo src="/video/run-05.mp4" srcMobile="/video/run-05-mobile.mp4" poster="/video/run-05-poster.jpg" opacity={0.3} testid="section-video-find-crew" />
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Reveal>
            <p className="font-mono-label text-white/40">THE DIRECTORY</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">FIND YOUR CREW.</h2>
            <p className="mt-4 max-w-lg text-white/60 text-sm">
              Search run clubs by city, schedule, pace, and the kind of community you want around you.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/clubs" data-testid="crew-directory-cta" className="btn-invert inline-flex items-center gap-2 border border-white/40 text-white font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink">
              BROWSE ALL CLUBS <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
        <div className="relative grid grid-cols-2 md:grid-cols-4 mt-14 border-t border-l border-white/10">
          {CITIES.map(([city, link], i) => {
            const citySlug = link.split("/").pop();
            return (
              <Link
                key={city}
                to={link}
                data-testid={`city-link-${city.toLowerCase().replace(/\s/g, "-")}`}
                className="group border-b border-r border-white/10 p-5 lg:p-6 hover:bg-white hover:text-ink transition-colors duration-300"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-label text-white/30 group-hover:text-ink/40">{String(i + 1).padStart(2, "0")}</span>
                  <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </div>
                <img
                  src={`/images/city-logos/${citySlug}.webp`}
                  alt={`${city} run club area code logo`}
                  loading="lazy"
                  width="640"
                  height="640"
                  className="mt-4 h-28 lg:h-36 w-full object-contain mix-blend-screen group-hover:mix-blend-multiply group-hover:invert transition-[filter] duration-300"
                />
                <p className="font-display text-lg lg:text-2xl mt-4 text-center">{city}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Choose your goal */}
      <section data-testid="choose-your-goal" className="relative bg-night px-5 lg:px-10 py-24 lg:py-32 border-b border-white/10">
        <SectionVideo src="/video/run-04.mp4" srcMobile="/video/run-04-mobile.mp4" poster="/video/run-04-poster.jpg" opacity={0.26} testid="section-video-goals" />
        <div className="relative">
        <Reveal>
          <p className="font-mono-label text-white/40">TRAIN FOR WHAT IS NEXT</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">CHOOSE YOUR GOAL.</h2>
        </Reveal>
        <div className="flex flex-wrap gap-3 mt-12">
          {GOALS.map((g) => (
            <Link
              key={g}
              to={GOAL_LINKS[g]}
              data-testid={`goal-chip-${g.toLowerCase().replace(/\s/g, "-")}`}
              className="btn-invert font-display text-2xl lg:text-4xl border border-white/20 px-6 lg:px-10 py-4 lg:py-6 hover:bg-white hover:text-ink"
            >
              {g}
            </Link>
          ))}
        </div>
        </div>
      </section>

      {/* Manifesto */}
      <section data-testid="manifesto-section" className="relative bg-ink px-5 lg:px-10 py-24 lg:py-32 border-b border-white/10">
        <SectionVideo src="/video/run-03.mp4" srcMobile="/video/run-03-mobile.mp4" poster="/video/run-03-poster.jpg" opacity={0.22} testid="section-video-manifesto" />
        <div className="relative">
        <Reveal>
          <p className="font-mono-label text-white/40">WHAT WE STAND ON</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">THE CLUB CODE.</h2>
        </Reveal>
        <div className="mt-14">
          {VALUES.map(([title, desc], i) => (
            <Reveal key={title} delay={i * 0.05}>
              <div className="group grid grid-cols-12 items-baseline gap-4 border-t border-white/10 py-8 lg:py-10 last:border-b hover:bg-white/[0.03] transition-colors duration-300">
                <span className="col-span-2 lg:col-span-1 font-mono-label text-white/30">0{i + 1}</span>
                <h3 className="col-span-10 lg:col-span-5 font-display text-3xl lg:text-5xl leading-none">{title}</h3>
                <p className="col-span-10 col-start-3 lg:col-span-5 lg:col-start-8 text-white/50 text-sm">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        </div>
      </section>

      {/* Training that works */}
      <section data-testid="training-preview" className="light-section bg-paper text-ink px-5 lg:px-10 py-24 lg:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Reveal>
            <p className="font-mono-label text-ink/40">EVIDENCE OVER HYPE</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">TRAINING THAT WORKS.</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/training" data-testid="training-hub-cta" className="btn-invert inline-flex items-center gap-2 border border-ink font-mono-label px-6 py-3.5 hover:bg-ink hover:text-paper">
              ALL TRAINING GUIDES <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <VideoTile src="/video/run-02.mp4" srcMobile="/video/run-02-mobile.mp4" srcWebm="/video/run-02.webm" poster="/video/run-02-poster.jpg" caption="TRACK MORNINGS" className="mt-14 h-56 lg:h-80 w-full" testid="training-video-strip" />
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10 mt-8">
          {articles.map((a, i) => (
            <Link key={a.slug} to={`/training/${a.slug}`} data-testid={`home-article-${a.slug}`} className="group bg-paper p-8 lg:p-10 hover:bg-ink hover:text-paper transition-colors duration-300">
              <p className="font-mono-label opacity-50">{a.category} · {a.read_time} MIN</p>
              <h3 className="font-display text-2xl lg:text-3xl mt-4 leading-tight">{a.title}</h3>
              <p className="text-sm opacity-60 mt-4">{a.excerpt}</p>
              <span className="inline-flex items-center gap-2 font-mono-label mt-6 opacity-70 group-hover:opacity-100">READ <ArrowRight size={12} /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* Club spotlight */}
      {spotlight && (
        <section data-testid="club-spotlight" className="bg-ink border-b border-white/10">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative h-[320px] lg:h-auto">
              <VideoTile src="/video/run-04.mp4" srcMobile="/video/run-04-mobile.mp4" srcWebm="/video/run-04.webm" poster="/video/run-04-poster.jpg" className="h-full w-full min-h-[320px]" testid="spotlight-video" />
              <div className="absolute inset-0 bg-ink/30 pointer-events-none" />
            </div>
            <div className="px-5 lg:px-14 py-16 lg:py-24">
              <p className="font-mono-label text-white/40">CLUB SPOTLIGHT</p>
              <div className="flex items-center gap-3 mt-5">
                <h2 className="font-display text-4xl lg:text-5xl leading-none">{spotlight.name}</h2>
                {spotlight.verified && <BadgeCheck className="text-white/60" size={22} />}
                {spotlight.guide && (
                  <span data-testid="spotlight-score" className="font-mono-label bg-white text-ink px-2.5 py-1.5">
                    SCORE {spotlight.guide.score}
                  </span>
                )}
              </div>
              <p className="font-mono-label text-white/40 mt-3">
                {spotlight.is_national ? "NATIONAL" : `${spotlight.city}, ${spotlight.state}`}
              </p>
              <p className="text-white/60 mt-6 max-w-md text-sm">{spotlight.description}</p>
              <div className="flex flex-wrap gap-4 mt-10">
                <Link to={`/clubs/${spotlight.slug}`} data-testid="spotlight-view-club" className="btn-invert bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white">
                  RUN WITH THEM
                </Link>
                <button
                  data-testid="spotlight-next-club"
                  onClick={() => setSpotIndex((v) => v + 1)}
                  className="btn-invert border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink"
                >
                  NEXT CLUB
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* From the miles - real footage */}
      <section data-testid="from-the-miles" className="bg-ink px-5 lg:px-10 py-24 lg:py-32 border-b border-white/10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Reveal>
            <p className="font-mono-label text-white/40">REAL FOOTAGE. REAL MILES.</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">FROM THE MILES.</h2>
            <p className="mt-4 max-w-lg text-white/60 text-sm">
              Shot at events we actually showed up for. Sandbags, wall balls, and a pack that would not break.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/stories" data-testid="stories-cta" className="btn-invert inline-flex items-center gap-2 border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink">
              READ THE STORIES <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 flex gap-px overflow-x-auto snap-x snap-mandatory lg:grid lg:grid-cols-5 bg-white/10 border border-white/10">
          {FILM.map((f, i) => (
            <Link to="/stories" key={i} className="snap-start shrink-0 w-[70vw] sm:w-[40vw] lg:w-auto">
              <VideoTile src={f.src} poster={f.poster} caption={f.caption} className="aspect-[4/5] h-full" testid={`film-tile-${i}`} />
            </Link>
          ))}
        </div>
      </section>

      {/* Built together */}
      <section data-testid="built-together" className="relative bg-night px-5 lg:px-10 py-24 lg:py-32 border-b border-white/10">
        <SectionVideo src="/video/run-02.mp4" srcMobile="/video/run-02-mobile.mp4" poster="/video/run-02-poster.jpg" opacity={0.26} testid="section-video-collabs" />
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono-label text-white/40">COLLABS</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]">
                YOUR CLUB. OUR PLATFORM. BUILT TOGETHER.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-end">
            <Reveal delay={0.15}>
              <p className="text-white/60 text-sm max-w-md">
                Co-branded merchandise, events, storytelling, sponsorships, and tools designed to help local running communities grow.
              </p>
              <Link to="/collabs" data-testid="build-collab-cta" className="btn-invert inline-block mt-8 bg-white text-ink font-mono-label px-8 py-4 border border-white hover:bg-transparent hover:text-white w-fit">
                BUILD A COLLAB
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Latest drop */}
      <section data-testid="latest-drop" className="relative bg-ink px-5 lg:px-10 py-24 lg:py-32 border-b border-white/10">
        <SectionVideo src="/video/run-01.mp4" srcMobile="/video/run-01-mobile.mp4" poster="/video/run-01-poster.jpg" opacity={0.2} testid="section-video-drop" />
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Reveal>
            <p className="font-mono-label text-white/40">THE SHOP</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">LATEST DROP.</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/shop" data-testid="shop-all-cta" className="btn-invert inline-flex items-center gap-2 border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink">
              SHOP ALL <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-14">
          {products.map((p) => (
            <Link to={p.url_slug ? `/products/${p.url_slug}` : "/shop"} key={p.slug} data-testid={`home-product-${p.slug}`} className="group bg-ink">
              <div className="hover-zoom aspect-square">
                <img src={p.image} alt={`${p.name} — Ninja Run Club apparel`} width="1024" height="1024" loading="lazy" className={`${p.color_art ? "" : "img-bw "}h-full w-full object-cover`} />
              </div>
              <div className="p-5 flex items-baseline justify-between gap-2">
                <p className="font-mono-label text-white/80">{p.name}</p>
                <p className="font-mono-label text-white/40">{p.price ? `$${p.price}` : "TBD"}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* City tee bundle promo */}
      <section data-testid="home-bundle-promo" className="relative bg-night px-5 lg:px-10 py-24 lg:py-32 border-b border-white/10">
        <SectionVideo src="/video/run-03.mp4" srcMobile="/video/run-03-mobile.mp4" poster="/video/run-03-poster.jpg" opacity={0.18} testid="section-video-bundle" />
        <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <Reveal>
            <p className="font-mono-label text-white/40">THE BUNDLE</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-none">ANY 2 CITY TEES. $75 FLAT.</h2>
            <p className="mt-4 max-w-lg text-white/60 text-sm">
              Rep your city and the one you race in. Thirty cities, skyline to full color — pick any pair, any sizes, and save up to $13.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/shop/city-series" data-testid="home-bundle-browse-cta" className="btn-invert inline-flex items-center gap-2 border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink">
              BROWSE ALL CITIES <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="relative mt-14">
            <BundleBuilder defaultSlug="color-city-dallas" hideHeader />
          </div>
        </Reveal>
      </section>

      {/* Join the movement */}
      <section data-testid="join-movement" className="light-section bg-paper text-ink px-5 lg:px-10 py-24 lg:py-36">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <Mark className="h-12 w-14" />
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[0.95]">JOIN THE MOVEMENT.</h2>
            <p className="mt-6 text-ink/60 max-w-md text-sm">
              You do not need to be fast. You need a reason to start and people who make you want to come back. Weekly runs, honest training, and a city near you.
            </p>
            <VideoTile src="/video/run-05.mp4" srcMobile="/video/run-05-mobile.mp4" srcWebm="/video/run-05.webm" poster="/video/run-05-poster.jpg" caption="SUNSET MILES" className="mt-10 aspect-video max-w-md" testid="join-video" />
          </Reveal>
          <Reveal delay={0.15}>
            <JoinForm source="homepage" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
